import { useMemo, useState } from 'react'
import { Modal } from '@/shared/components/Modal'
import { EmptyState, PageLoading } from '@/shared/components/EmptyState'
import { PageIntro } from '@/shared/components/PageIntro'
import { RegistryIcon } from '@/shared/components/RepoProviderIcon'
import { Tooltip } from '@/shared/components/Tooltip'
import { ProviderCodeModal } from '@/features/providers/components/ProviderCodeModal'
import { ManageProviderModal } from '@/features/providers/components/ManageProviderModal'
import { ProviderPoliciesPanel } from '@/features/providers/components/ProviderPoliciesModal'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import { ServeModeControl, ServeModeBadge } from '@/shared/components/ServeOverrideControl'
import { useToast } from '@/context/ToastContext'
import type { ProviderArtefactVersion, ServeMode } from '@/lib/apiTypes'
import { useProviderArtefact, patchProviderArtefact, removeProvider } from '@/lib/hooks'
import { formatCount } from '@/lib/format'
import { ActionRow } from '@/features/providers/components/ActionRow'
import { ConstraintCell } from '@/features/providers/components/ConstraintCell'
import { DiscoveredMark } from '@/features/providers/components/DiscoveredMark'
import { DownloadsSeriesChart } from '@/features/providers/components/DownloadsSeriesChart'
import { cvssColor } from '@/features/providers/components/MetricsCell'
import { SourceLink } from '@/features/providers/components/SourceLink'
import { StatCell } from '@/shared/components/StatCell'
import { VcsStatsHeader } from '@/features/providers/components/VcsStatsHeader'
import { VersionRow } from '@/features/providers/components/VersionRow'
import { compareVersionsDesc, registryLabel } from '@/features/providers/lib/versions'
import { ARTEFACT_TABS, type ArtefactTab } from '@/features/providers/tabs/artefactTabs'
import { type OrgLimits } from '@/features/providers/types'
import { Tabs } from '@/shared/components/Tabs'

/**
 * Per-artefact detail (GET /providers/:upstream/:namespace/:type) in a modal, per house convention.
 * Identity row → tab bar (Insight / Versions / Policies / Actions) → the active tab's content,
 * each opening with its own dismissible "what is this tab for" explainer:
 *  - Insight: the KPI band, downloads trend chart, and VCS repo-health stats.
 *  - Versions: the full version history table (per-version vulnerabilities/usage/refs expand inline).
 *  - Policies: this provider's policy overrides - no longer a modal of its own; the table's
 *    Policies button lands straight here via `initialTab`.
 *  - Actions: Consume config for everyone; serve mode / edit constraint / remove for admins,
 *    laid out as a settings list (label + description on the left, controls on the right).
 */
export function ArtefactDetailModal({ orgId, upstream, namespace, type, canAdmin, limits, initialTab, initialVersion, onClose, onChanged, onManaged }: {
  orgId: string
  /** Registry the artefact belongs to - part of its identity alongside namespace/type. */
  upstream: string
  namespace: string
  type: string
  canAdmin: boolean
  limits: OrgLimits['limits']
  /** Which tab to open on. The table's Policies button sets this to 'policies'; a plain row
   *  click leaves it at 'insight'. Only read once (useState) - this component is conditionally
   *  rendered, so every open is a fresh mount. */
  initialTab: ArtefactTab
  /** Version to open expanded, from `?v=`. Forces the Versions tab, since landing on Insights
   *  with a row silently expanded underneath would hide the thing that was linked to. Read once,
   *  like initialTab - the modal remounts per open. */
  initialVersion?: string | null
  onClose: () => void
  /** Notify the list page after a mutation (serve override, remove) so its rows refresh too. */
  onChanged: () => void
  /** Fired after an edit save - same targeted stats refresh the "Add provider" flow kicks off. */
  onManaged: (upstream: string, ns: string, type: string) => void
}) {
  const detail = useProviderArtefact(orgId, upstream, namespace, type)
  const data = detail.data
  const { showToast } = useToast()
  const [tab, setTab] = useState<ArtefactTab>(initialVersion ? 'versions' : initialTab)
  const [showEdit, setShowEdit] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [actionBusy, setActionBusy] = useState<string | null>(null)
  const versions = useMemo(
    () => (data?.versions ?? []).slice().sort((a, b) => compareVersionsDesc(a.version, b.version)),
    [data],
  )
  // Newest build by BASE version (versions are already newest-first, so the first row
  // carrying a `build` object wins). Drives the "terramantle version" KPI cell.
  const latestBuild = useMemo(() => {
    const row = versions.find((v) => v.build)
    return row && row.build ? { base: row.version, build: row.build } : null
  }, [versions])
  // The KPI band describes the LATEST version only (versions are newest-first). A cross-version
  // worst-case rollup used to live here, but an ancient release with a decade-old unfixed CVE
  // made the strip read as if the provider were currently in that state.
  const latest = versions[0] ?? null
  // Null when unscanned: render "—", never "no findings" (see ProviderArtefactVersion.scanned).
  const scannedLatest = latest?.scanned ? latest : null

  // Which version's consume affordance is currently open (null = none). Used-by / referenced-by
  // moved INSIDE the row expansion as tabs - no separate modals anymore.
  const [consumeVersion, setConsumeVersion] = useState<ProviderArtefactVersion | null>(null)
  // Inline expansion in the versions table - ONE row at a time (expanding another
  // collapses the first: repeated height jumps inside the table's scroll container disorient).
  // Not in the URL - a conscious choice, the old per-version findings modal wasn't deep-linkable.
  const [expandedVersion, setExpandedVersion] = useState<string | null>(initialVersion ?? null)

  const serveGated = !limits.providerServe
  // Remove only makes sense for a hand-added artefact nothing references: an auto-discovered one
  // would be recreated by the next scan, so the honest "stop serving" control there is Off / Block.
  const removable = !!data && !data.discovered && data.usage_workspace_count === 0 && data.referenced_module_count === 0

  async function applyServeMode(mode: ServeMode) {
    if (!data) return
    setActionBusy('serve_mode')
    try {
      await patchProviderArtefact(orgId, upstream, namespace, type, { serve_mode: mode })
      showToast(
        mode === 'block' ? `${namespace}/${type} blocked - nothing serves until set back to Serve.`
        : mode === 'off' ? `${namespace}/${type} set to Off - config kept, not served.`
        : `${namespace}/${type} is now serving.`,
      )
      detail.refetch()
      onChanged()
    } catch (e) {
      showToast((e as Error).message, 'error')
    } finally {
      setActionBusy(null)
    }
  }

  async function removeArtefact() {
    setActionBusy('remove')
    try {
      await removeProvider(orgId, upstream, namespace, type)
      showToast(`${namespace}/${type} (${registryLabel(upstream)}) removed from this org.`)
      setConfirmRemove(false)
      onChanged()
      onClose()
    } catch (e) {
      // The server carries the reason (referenced) in the error message.
      showToast((e as Error).message, 'error')
    } finally {
      setActionBusy(null)
    }
  }

  return (
    <Modal track="provider_artefact_detail" title={`${namespace}/${type}`} kicker="// provider artefact" maxWidth={1240} onClose={onClose}
      footer={<div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button></div>}
    >
      {detail.loading ? (
        <PageLoading />
      ) : detail.error && !data ? (
        <div className="feedback-error">{String(detail.error)}</div>
      ) : !data ? (
        <EmptyState icon="⬡" title="Provider not found" subtitle="It may have been removed." />
      ) : (
        <>
          {/* A failed background refresh keeps the last-good dossier on screen (useAsync retains
              data) and says so inline, rather than unmounting everything the user was reading. */}
          {detail.error && (
            <div role="status" className="feedback-error" style={{ marginBottom: 12 }}>
              Refresh failed - showing the last loaded data. {String(detail.error)}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', alignItems: 'center', marginBottom: 16, fontSize: '0.76rem' }}>
            <DiscoveredMark row={data} />
            <ServeModeBadge mode={data.serve_mode} />
            <span className="inline-dim">
              registry{' '}
              <span className="dim-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <RegistryIcon upstream={data.upstream} size={12} />
                {registryLabel(data.upstream)}
              </span>
            </span>
            <span className="inline-dim">
              constraint <ConstraintCell value={data.version_constraint} />
            </span>
            {latest && (
              <span className="inline-dim">
                latest <code className="mono dim-badge">v{latest.version}</code>
              </span>
            )}
            <span className="inline-dim">
              blast radius{' '}
              <span className="dim-badge">
                {data.usage_workspace_count} workspace{data.usage_workspace_count === 1 ? '' : 's'}
              </span>
            </span>
            <span className="inline-dim">
              referenced by{' '}
              <span className="dim-badge">
                {data.referenced_module_count} module{data.referenced_module_count === 1 ? '' : 's'}
              </span>
            </span>
            {data.source_url && <SourceLink url={data.source_url} />}
          </div>

          <VcsStatsHeader stats={data.vcs_stats} latestBuild={latestBuild} />

          <Tabs
            tabs={ARTEFACT_TABS}
            value={tab}
            onChange={setTab}
            label="Provider sections"
          />

          {tab === 'insight' && (
            <>
              <PageIntro pageKey="provider-detail-insight" title="What is this tab?">
                <p style={{ margin: 0 }}>
                  A KPI snapshot of the <strong>latest version only</strong> (an older version may carry
                  different findings - see Versions for the full breakdown), 90-day download trends and
                  patch adoption, and the source repository's health stats.
                </p>
              </PageIntro>

              {/* KPI band — the Dashboard analytics stat-strip idiom, full width. */}
              <div className="stat-strip fu" style={{ marginBottom: 16 }}>
                <StatCell
                  label="critical · high · latest"
                  value={scannedLatest ? `${formatCount(scannedLatest.critical_count)} · ${formatCount(scannedLatest.high_count)}` : '—'}
                  color={!scannedLatest ? 'var(--text3)' : scannedLatest.critical_count > 0 ? 'var(--red)' : scannedLatest.high_count > 0 ? 'var(--amber)' : undefined}
                />
                <StatCell label="in use · workspaces" value={formatCount(data.usage_workspace_count)} />
                <StatCell label="referenced · modules" value={formatCount(data.referenced_module_count)} />
                <StatCell
                  label="max cvss · latest"
                  value={scannedLatest?.max_cvss != null ? scannedLatest.max_cvss.toFixed(1) : '—'}
                  color={scannedLatest?.max_cvss != null ? cvssColor(scannedLatest.max_cvss) : 'var(--text3)'}
                />
                <StatCell label="downloads · 30d" value={formatCount(data.downloads.last_30d)} />
                <StatCell label="terramantle version">
                  {latestBuild ? (
                    <Tooltip text={`TerraMantle's forked and patched version of this provider: revision ${latestBuild.build.patch_rev} of upstream v${latestBuild.base}${latestBuild.build.fixed_count > 0 ? `, fixing ${latestBuild.build.fixed_count} CVE${latestBuild.build.fixed_count === 1 ? '' : 's'}` : ', which needed no CVE fixes'}. Expand that version for details; Consume it to pin.`} placement="top">
                      <code className="mono" style={{ fontSize: '0.85rem', cursor: 'help' }}>{latestBuild.build.full_version}</code>
                    </Tooltip>
                  ) : (
                    <span className="dim">—</span>
                  )}
                </StatCell>
              </div>

              {/* Downloads insights — full-width analytics card (Dashboard chart-card shape). */}
              <div className="card fu" style={{ marginBottom: 16 }}>
                <div className="card-header">
                  <div>
                    <div className="kicker">// downloads</div>
                    <h2 style={{ fontSize: '0.95rem' }}>Insights · last 90 days</h2>
                  </div>
                </div>
                <div style={{ padding: '16px 18px' }}>
                  {data.downloads.total === 0 ? (
                    <EmptyState
                      icon="⇩"
                      title="No downloads recorded yet"
                      subtitle="This accrues as versions are served to your workspaces through the org registry."
                    />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                      <div className="stat-strip">
              {/* Pure-CSS three-bar chart (30d / 60d / 90d), scaled to the largest of the three — no chart    library. Bars are var(--accent)-filled divs. */}
                        <StatCell label="30 days" value={formatCount(data.downloads.last_30d)} />
                        <StatCell label="60 days" value={formatCount(data.downloads.last_60d)} />
                        <StatCell label="90 days" value={formatCount(data.downloads.last_90d)} />
                        <StatCell label="total" value={formatCount(data.downloads.total)} />
                        {data.downloads.patched.patched_90d > 0 && (
                          <StatCell
                            label="built · 90d"
                            value={formatCount(data.downloads.patched.patched_90d)}
                            color="var(--green)"
                          />
                        )}
                      </div>
                      {(data.downloads.series_90d?.length ?? 0) > 0 && (
                        <DownloadsSeriesChart
                          series={data.downloads.series_90d}
                          builtVersions={new Set(data.versions.flatMap((v) => v.build ? [v.build.full_version] : []))}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {tab === 'versions' && (
            <>
              <PageIntro pageKey="provider-detail-versions" title="What is this tab?">
                <p style={{ margin: 0 }}>
                  Every version TerraMantle has verified and built for this provider, newest first. Expand a row for
                  its vulnerability findings, which workspaces lock it, and which modules reference it. The
                  Build column shows TerraMantle's own CVE-patched rebuild of that version, when one exists it will be automatically served.
                  TerraMantle does not serve upstream providers.
                </p>
              </PageIntro>

              {versions.length === 0 ? (
                <EmptyState
                  icon="◈"
                  title="No versions listed yet"
                  subtitle="Versions are listed once TerraMantle has verified them. Newly discovered providers are populated within about 15 minutes; hand-added ones usually take a few minutes."
                />
              ) : (
                <div className="table-wrap table-scroll fu" style={{ maxHeight: 800 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>
                          <Tooltip text="Upstream version. Expand a row for its vulnerabilities, live usage and module references." placement="top">
                            <span tabIndex={0} className="cursor-help">Version</span>
                          </Tooltip>
                        </th>
                        <th>
                          <Tooltip text="Link out to this version's release page on the source repository." placement="top">
                            <span tabIndex={0} className="cursor-help">Release</span>
                          </Tooltip>
                        </th>
                        <th>
                          <Tooltip text="When this version was published upstream." placement="top">
                            <span tabIndex={0} className="cursor-help">Released</span>
                          </Tooltip>
                        </th>
                        <th>
                          <Tooltip text="terramantle's own build of this upstream version, if one is published - with CVE fixes when any were fixable. Click it for the build's details and the exact-pin snippet." placement="top">
                            <span tabIndex={0} className="cursor-help">Build</span>
                          </Tooltip>
                        </th>
                        <th>
                          <Tooltip text="Workspaces actively using this exact version - discovered from state workspace lock files." placement="top">
                            <span tabIndex={0} className="cursor-help">Used by</span>
                          </Tooltip>
                        </th>
                        <th>
                          <Tooltip text="Modules whose required_providers admit this version - discovered from module source; declared, not necessarily in use." placement="top">
                            <span tabIndex={0} className="cursor-help">Referenced</span>
                          </Tooltip>
                        </th>
                        <th>
                          <Tooltip text="Findings from this version's last completed scan, by severity. Click to open the breakdown." placement="top">
                            <span tabIndex={0} className="cursor-help">Vulnerabilities</span>
                          </Tooltip>
                        </th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {versions.map((v) => (
                        <VersionRow
                          key={v.version}
                          orgId={orgId}
                          upstream={upstream}
                          namespace={namespace}
                          type={type}
                          sourceUrl={data.source_url}
                          v={v}
                          canAdmin={canAdmin}
                          expanded={expandedVersion === v.version}
                          onToggleExpand={() => setExpandedVersion(expandedVersion === v.version ? null : v.version)}
                          onExpand={() => setExpandedVersion(v.version)}
                          onConsume={() => setConsumeVersion(v)}
                          onChanged={() => { detail.refetch(); onChanged() }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {tab === 'policies' && (
            <>
              <PageIntro pageKey="provider-detail-policies" title="What is this tab?">
                <p style={{ margin: 0 }}>
                  Policy overrides scoped to this one provider: override the org's inherited baseline, or
                  author custom Rego policies that apply only to <code className="mono">{namespace}/{type}</code>.
                </p>
              </PageIntro>
              <ProviderPoliciesPanel orgId={orgId} namespace={namespace} type={type} canAdmin={canAdmin} />
            </>
          )}

          {tab === 'actions' && (
            <>
              <PageIntro pageKey="provider-detail-actions" title="What is this tab?">
                <p style={{ margin: 0 }}>
                  Consume this provider's config, pinned to its latest version. Admins can also control
                  whether it's served to workspaces, edit its version constraint, or remove it from the org.
                </p>
              </PageIntro>

              <ActionRow
                title="Consume config"
                description={`Copy the required_providers block, registry endpoint and CLI steps pinned to v${latest?.version ?? '?'}.`}
                last={!canAdmin}
              >
                <button
                  className="btn btn-sm"
                  disabled={!latest}
                  title={latest ? undefined : 'No cached versions to consume yet.'}
                  onClick={() => latest && setConsumeVersion(latest)}
                >
                  Consume config
                </button>
              </ActionRow>

              {canAdmin && (
                <>
                  <ActionRow
                    title="Serve mode"
                    description="Off keeps the config but serves nothing. Serve makes it available to this org (policies still apply). Block hard-refuses it regardless of policy."
                  >
                    <ServeModeControl
                      value={data.serve_mode}
                      subject={`${namespace}/${type}`}
                      busy={actionBusy === 'serve_mode'}
                      serveDisabledReason={serveGated ? 'Serving providers requires the Business plan.' : null}
                      onApply={applyServeMode}
                    />
                  </ActionRow>

                  <ActionRow
                    title="Version constraint"
                    description="Change which upstream versions this org serves. Newly matched versions are scanned automatically. The registry is part of the provider's identity - add it again to serve it from the other registry."
                    last={!removable}
                  >
                    <button className="btn btn-sm" onClick={() => setShowEdit(true)}>Edit constraint</button>
                  </ActionRow>

                  {removable && (
                    <ActionRow
                      title={<span style={{ color: 'var(--red)' }}>Remove provider</span>}
                      description="Remove this provider from the org. It was added by hand and nothing references it; scan history for the shared cache is kept. If a module or workspace references it later, it's discovered again automatically."
                      last
                    >
                      <button
                        className="btn btn-danger btn-sm"
                        disabled={actionBusy === 'remove'}
                        onClick={() => setConfirmRemove(true)}
                      >
                        {actionBusy === 'remove' ? <span className="spinner" /> : 'Remove'}
                      </button>
                    </ActionRow>
                  )}
                </>
              )}
            </>
          )}

          {consumeVersion && (
            <ProviderCodeModal
              orgId={orgId}
              upstream={upstream}
              namespace={namespace}
              type={type}
              version={consumeVersion.version}
              patchedVersion={consumeVersion.build?.full_version ?? null}
              onClose={() => setConsumeVersion(null)}
            />
          )}

          {showEdit && (
            <ManageProviderModal
              orgId={orgId}
              initial={{
                namespace,
                type,
                upstream: data.upstream,
                version_constraint: data.version_constraint,
              }}
              onClose={() => setShowEdit(false)}
              onCreated={(u, n, t) => { onManaged(u, n, t); detail.refetch() }}
            />
          )}

          {confirmRemove && (
            <ConfirmModal
              track="provider_remove_confirm"
              title={`Remove ${namespace}/${type}?`}
              message={
                <>
                  Your org registry stops serving <strong>{namespace}/{type}</strong>, so{' '}
                  <code className="mono">tofu init</code> will no longer resolve it from Terramantle, and it
                  leaves the providers list. If a module or workspace references it later it is discovered
                  again automatically. You can also add it back by hand at any time.
                </>
              }
              confirmLabel="Remove"
              busy={actionBusy === 'remove'}
              onConfirm={removeArtefact}
              onCancel={() => setConfirmRemove(false)}
            />
          )}
        </>
      )}
    </Modal>
  )
}

